import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Loader2, Plus, Search, UserCheck, UserX, Save, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { ticketsApi, type QueueQuery, type TicketBrief, type TicketStatus, type TicketPriority } from '../api/tickets';
import { authApi } from '../api/auth';
import { orgsApi, type Organization } from '../api/organizations';
import { OrgCombobox } from '../components/OrgCombobox';
import { useAuth, type AuthUser } from '../lib/auth';
import { fmtTime, ticketNumber, STATUS_LABEL, STATUS_COLOR, PRIORITY_COLOR, SLA_COLOR, PROJECT_LABEL, PROJECT_BADGE, ticketStatusLabel, ticketStatusColor } from '../lib/format';
import { BUILTIN_VIEWS, loadSavedViews, saveView, deleteView, type SavedView } from '../lib/views';

const STATUS_FILTERS: Array<{ value?: TicketStatus; label: string }> = [
  { value: undefined, label: 'Все' },
  { value: 'new', label: 'Новые' },
  { value: 'open', label: 'В работе' },
  { value: 'pending_customer', label: 'Ждём' },
  { value: 'resolved', label: 'Решённые' },
];
const PRIORITY_FILTERS: Array<{ value?: TicketPriority; label: string }> = [
  { value: undefined, label: 'Все' }, { value: 'P1', label: 'P1' }, { value: 'P2', label: 'P2' },
  { value: 'P3', label: 'P3' }, { value: 'P4', label: 'P4' },
];

export default function QueuePage() {
  const { user } = useAuth();
  const isStaff = user?.type === 'staff';

  // URL-params: позволяет открывать очередь сразу с фильтром по агенту /
  // организации / статусу. Используется drill-down ссылками с Дашборда.
  //
  // Все фильтры живут в адресной строке: раньше они были только в state, поэтому
  // после захода в тикет и возврата «К списку» очередь открывалась с нуля —
  // отобранные тикеты приходилось искать заново.
  const initialQuery = (): QueueQuery => {
    const sp = new URLSearchParams(window.location.search);
    const q: QueueQuery = {
      sort_by: (sp.get('sort_by') as QueueQuery['sort_by']) || 'created_at',
      sort_dir: (sp.get('sort_dir') as QueueQuery['sort_dir']) || 'desc',
    };
    const a = sp.get('agent_id') || sp.get('assignee'); if (a) q.assignee = a;
    const o = sp.get('organization_id'); if (o) q.organization_id = o;
    const s = sp.get('status'); if (s) q.status = s as any;
    const p = sp.get('priority'); if (p) q.priority = p as any;
    const search = sp.get('q'); if (search) q.q = search;
    return q;
  };
  const [query, setQuery] = useState<QueueQuery>(initialQuery);
  const [hideClosed, setHideClosed] = useState<boolean>(() =>
    localStorage.getItem('queue.hideClosed') !== '0'  // default true
  );
  const [items, setItems] = useState<TicketBrief[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<Record<string, number>>({});
  const [staff, setStaff] = useState<AuthUser[]>([]);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [views, setViews] = useState<SavedView[]>(loadSavedViews());
  const [bulkOpen, setBulkOpen] = useState(false);

  // Держим фильтры в адресной строке (replaceState — чтобы «назад» в браузере не
  // отматывал каждое нажатие чипа). Этот же адрес запоминаем как «куда вернуться»
  // из карточки тикета: кнопка «К списку» ведёт на него, а не на голую очередь.
  useEffect(() => {
    const sp = new URLSearchParams();
    if (query.status) sp.set('status', String(query.status));
    if (query.priority) sp.set('priority', String(query.priority));
    if (query.assignee) sp.set('assignee', query.assignee);
    if (query.organization_id) sp.set('organization_id', query.organization_id);
    if (query.q) sp.set('q', query.q);
    if (query.sort_by && query.sort_by !== 'created_at') sp.set('sort_by', query.sort_by);
    if (query.sort_dir && query.sort_dir !== 'desc') sp.set('sort_dir', query.sort_dir);
    const url = sp.toString() ? `${window.location.pathname}?${sp}` : window.location.pathname;
    window.history.replaceState(null, '', url);
    try { sessionStorage.setItem('queue.returnUrl', url); } catch { /* приватный режим */ }
  }, [query]);

  const PAGE_SIZE = 50;
  const load = async (q: QueueQuery, append = false) => {
    setLoading(true);
    try {
      const offset = append ? items.length : 0;
      const r = await ticketsApi.list({ ...q, limit: PAGE_SIZE, offset, hide_closed: hideClosed });
      const arr = Array.isArray(r?.items) ? r.items : [];
      setItems(prev => append ? [...prev, ...arr] : arr);
      setTotal(typeof r?.total === 'number' ? r.total : 0);
    } finally { setLoading(false); }
  };
  const loadMore = () => load(query, true);

  useEffect(() => { load(query, false); }, [JSON.stringify(query), hideClosed]);
  useEffect(() => {
    if (!isStaff) return;
    authApi.staff().then(setStaff).catch(() => {});
    orgsApi.list().then(setOrgs).catch(() => {});
  }, [isStaff]);
  // Сводка по статусам — обновляется при перезагрузке списка
  useEffect(() => {
    ticketsApi.stats().then(setStats).catch(() => {});
  }, [items.length]);

  const applyView = (v: SavedView) => setQuery({ ...v.query });

  const onSaveView = () => {
    const name = window.prompt('Название view:');
    if (!name) return;
    const v = { id: 'u_' + Date.now(), name, query: { ...query } };
    saveView(v);
    setViews(loadSavedViews());
  };
  const onDeleteView = (id: string) => {
    if (!window.confirm('Удалить view?')) return;
    deleteView(id);
    setViews(loadSavedViews());
  };

  const setPart = (patch: Partial<QueueQuery>) => setQuery(s => ({ ...s, ...patch }));

  // Статус-сводка над фильтрами. Клик — фильтр по статусу.
  const STATUS_BAR: Array<{ key: string; label: string; color: string }> = [
    { key: 'total',            label: 'Всего',   color: 'bg-slate-800 text-slate-200' },
    { key: 'new',              label: 'Новых',   color: 'bg-blue-900/40 text-blue-300' },
    { key: 'open',             label: 'В работе', color: 'bg-yellow-900/40 text-yellow-300' },
    { key: 'pending_customer', label: 'Ждём',    color: 'bg-orange-900/40 text-orange-300' },
    { key: 'l3',               label: 'На L3',   color: 'bg-amber-900/40 text-amber-300' },
    { key: 'on_hold',          label: 'Пауза',   color: 'bg-purple-900/30 text-purple-300' },
    { key: 'resolved',         label: 'Решено',  color: 'bg-emerald-900/40 text-emerald-300' },
    { key: 'closed',           label: 'Закрыто', color: 'bg-slate-800/60 text-slate-400' },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-4">
      {/* ── SIDEBAR с фильтрами ────────────────────────────────────────────── */}
      <aside className="space-y-3 self-start">
        {/* Создать */}
        <Link to="/new"
          className="w-full inline-flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-500 text-white text-sm font-bold px-3 py-2 rounded">
          <Plus size={15} /> Новый тикет
        </Link>

        {/* Статусы — вертикальный стек */}
        <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-2">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 px-2 py-1">Статусы</div>
          {STATUS_BAR.map(s => {
            const n = stats[s.key] ?? 0;
            const isActive = s.key === 'total' ? !query.status : query.status === s.key;
            return (
              <button key={s.key}
                onClick={() => setPart({ status: s.key === 'total' ? undefined : s.key as TicketStatus })}
                className={`w-full flex items-center justify-between px-2 py-1.5 rounded text-xs transition ${
                  isActive ? 'bg-blue-600/20 text-blue-200 ring-1 ring-blue-500/60' : 'text-slate-300 hover:bg-slate-800'
                }`}>
                <span>{s.label}</span>
                <span className="font-bold font-mono text-slate-200">{n}</span>
              </button>
            );
          })}
        </div>

        {/* Приоритет */}
        <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-2">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 px-2 py-1">Приоритет</div>
          <div className="flex gap-1 px-1 pb-1">
            {PRIORITY_FILTERS.map(o => (
              <button key={o.label} onClick={() => setPart({ priority: o.value })}
                className={`flex-1 px-2 py-1 rounded text-[10px] font-bold uppercase transition ${
                  query.priority === o.value ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
                }`}>
                {o.label}
              </button>
            ))}
          </div>
        </div>

        {/* Агент + Клиент */}
        {isStaff && (
          <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-2 space-y-2">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 px-2 py-1">Агент</div>
              <select value={query.assignee || ''} onChange={e => setPart({ assignee: e.target.value || undefined })}
                className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-xs text-white focus:border-blue-500 outline-none">
                <option value="">Все агенты</option>
                <option value="me">Мои</option>
                <option value="unassigned">Без агента</option>
                {staff.map(s => <option key={s.id} value={s.id}>{s.full_name || s.email}</option>)}
              </select>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 px-2 py-1">Клиент</div>
              <OrgCombobox
                value={query.organization_id || null}
                onChange={(id) => setPart({ organization_id: id || undefined })}
                options={orgs} placeholder="Все клиенты" clearable
                className="w-full"
              />
            </div>
          </div>
        )}

        {/* Views */}
        <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-2">
          <div className="flex items-center justify-between px-2 py-1">
            <span className="text-[10px] uppercase tracking-wider text-slate-500">Views</span>
            <button onClick={onSaveView} title="Сохранить текущие фильтры"
              className="text-[10px] inline-flex items-center gap-0.5 text-slate-500 hover:text-white">
              <Save size={10} /> save
            </button>
          </div>
          <div className="flex flex-wrap gap-1 px-1 pb-1">
            {BUILTIN_VIEWS.map(v => (
              <button key={v.id} onClick={() => applyView(v)}
                className="px-2 py-0.5 rounded-full text-[10px] bg-slate-800 hover:bg-slate-700 text-slate-300">
                {v.name}
              </button>
            ))}
            {views.map(v => (
              <span key={v.id} className="inline-flex items-center bg-blue-900/30 border border-blue-800 rounded-full text-[10px]">
                <button onClick={() => applyView(v)} className="px-2 py-0.5 text-blue-300 hover:text-white">{v.name}</button>
                <button onClick={() => onDeleteView(v.id)} className="px-1 py-0.5 text-slate-500 hover:text-red-400">
                  <Trash2 size={9} />
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Hide closed */}
        <label className="flex items-center gap-2 text-[11px] text-slate-400 cursor-pointer px-2 py-1">
          <input type="checkbox" checked={hideClosed}
                 onChange={e => {
                   setHideClosed(e.target.checked);
                   localStorage.setItem('queue.hideClosed', e.target.checked ? '1' : '0');
                 }}
                 className="accent-blue-500" />
          Скрыть закрытые/решённые
        </label>
      </aside>

      {/* ── ОСНОВНАЯ ЧАСТЬ ──────────────────────────────────────────────────── */}
      <div className="space-y-3 min-w-0">
        {/* Поиск + быстрые фильтры по статусу (горизонтально) */}
        <div className="flex flex-wrap gap-2 items-center bg-slate-900/40 border border-slate-800 rounded-xl p-2">
          <MultiPills options={STATUS_FILTERS} value={query.status} onChange={v => setPart({ status: v as any })} />
          {isStaff && items.length > 0 && (
            <button onClick={() => setBulkOpen(true)}
                    title="Массовое действие над всеми тикетами в текущем фильтре"
                    className="text-[10px] font-bold uppercase px-2.5 py-1 rounded-full bg-purple-700 hover:bg-purple-600 text-white">
              Массовое… ({items.length})
            </button>
          )}
          <div className="relative ml-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={14} />
            <input value={query.q || ''} onChange={e => setPart({ q: e.target.value || undefined })}
              placeholder="Тема, клиент, автор, SD-NNNN…"
              className="bg-slate-950 border border-slate-700 rounded-full py-1.5 pl-9 pr-3 text-xs text-white focus:border-blue-500 outline-none w-80" />
          </div>
        </div>

        <VirtualizedTable items={items}
          loading={loading} total={total}
                          sortBy={query.sort_by || 'created_at'}
                          sortDir={query.sort_dir || 'desc'}
                          onSort={(col, dir) => setPart({ sort_by: col, sort_dir: dir })}
                          isStaff={isStaff}
                          staff={staff}
                          onAssign={async (ticketId, assigneeId) => {
                            await ticketsApi.assign(ticketId, { assignee_id: assigneeId });
                            load(query);
                          }}
                          onLoadMore={loadMore} />
      </div>

      {bulkOpen && (
        <BulkActionModal items={items} staff={staff}
                         onClose={() => setBulkOpen(false)}
                         onApplied={() => { setBulkOpen(false); load(query); }} />
      )}
    </div>
  );
}

function BulkActionModal({ items, staff, onClose, onApplied }:
  { items: TicketBrief[]; staff: AuthUser[]; onClose: () => void; onApplied: () => void }) {
  const [action, setAction] = useState<'set_status' | 'set_priority' | 'set_assignee' | 'close'>('set_status');
  const [value, setValue] = useState<string>('open');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  const ids = items.map(t => t.id);

  // Меняем дефолт value при смене action.
  useEffect(() => {
    if (action === 'set_status') setValue('open');
    else if (action === 'set_priority') setValue('P3');
    else if (action === 'set_assignee') setValue(staff[0]?.id || 'unassign');
    else setValue('');
  }, [action, staff]);

  const submit = async () => {
    if (!window.confirm(`Применить «${action}» (${value}) к ${ids.length} тикетам?`)) return;
    setBusy(true);
    try {
      const r = await ticketsApi.bulkAction(ids, action, value || undefined);
      setResult(`Обновлено ${r.updated} из ${ids.length} (пропущено ${r.skipped})`);
      setTimeout(onApplied, 1200);
    } catch (e: any) {
      setResult('Ошибка: ' + (e?.response?.data?.detail || e?.message));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-xl p-5 w-full max-w-md space-y-3">
        <h3 className="font-bold text-white text-lg">Массовое действие</h3>
        <p className="text-sm text-slate-400">
          Тикетов в текущем фильтре: <b className="text-white">{ids.length}</b>.
          Действие применится ко всем загруженным.
        </p>
        <div>
          <label className="text-xs text-slate-400 block mb-1">Действие</label>
          <select value={action} onChange={e => setAction(e.target.value as any)}
                  className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm text-white">
            <option value="set_status">Сменить статус</option>
            <option value="set_priority">Сменить приоритет</option>
            <option value="set_assignee">Назначить агента</option>
            <option value="close">Закрыть (status=closed)</option>
          </select>
        </div>
        {action !== 'close' && (
          <div>
            <label className="text-xs text-slate-400 block mb-1">Значение</label>
            {action === 'set_status' && (
              <select value={value} onChange={e => setValue(e.target.value)}
                      className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm text-white">
                <option value="new">Новый</option>
                <option value="open">В работе</option>
                <option value="pending_customer">Ждём клиента</option>
                <option value="on_hold">На паузе</option>
                <option value="resolved">Решён</option>
                <option value="closed">Закрыт</option>
              </select>
            )}
            {action === 'set_priority' && (
              <select value={value} onChange={e => setValue(e.target.value)}
                      className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm text-white">
                <option value="P1">P1</option><option value="P2">P2</option>
                <option value="P3">P3</option><option value="P4">P4</option>
              </select>
            )}
            {action === 'set_assignee' && (
              <select value={value} onChange={e => setValue(e.target.value)}
                      className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm text-white">
                <option value="unassign">— снять назначение —</option>
                {staff.map(s => <option key={s.id} value={s.id}>{s.full_name || s.email}</option>)}
              </select>
            )}
          </div>
        )}
        {result && <div className={`text-xs ${result.startsWith('Ошибка') ? 'text-red-400' : 'text-emerald-400'}`}>{result}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} disabled={busy}
                  className="text-xs px-3 py-1.5 rounded border border-slate-700 text-slate-300 hover:bg-slate-800">
            Отмена
          </button>
          <button onClick={submit} disabled={busy || ids.length === 0}
                  className="text-xs px-3 py-1.5 rounded bg-purple-700 hover:bg-purple-600 text-white font-bold">
            {busy ? <Loader2 size={12} className="animate-spin inline" /> : `Применить к ${ids.length}`}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Чипы статусов с накоплением: можно держать на экране, например, «Новые» и «В работе»
 * одновременно. Значение — те же строки статусов через запятую, что понимает API.
 * «Все» сбрасывает выбор.
 */
function MultiPills({ options, value, onChange }:
  { options: Array<{ value?: string; label: string }>; value?: string; onChange: (v: string | undefined) => void }) {
  const selected = (value || '').split(',').filter(Boolean);
  const toggle = (v?: string) => {
    if (!v) return onChange(undefined);                       // «Все»
    const next = selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v];
    onChange(next.length ? next.join(',') : undefined);
  };
  return (
    <div className="flex gap-1">
      {options.map(o => {
        const on = o.value ? selected.includes(o.value) : selected.length === 0;
        return (
          <button key={o.label} onClick={() => toggle(o.value)}
            title={o.value ? 'Можно выбрать несколько' : 'Сбросить фильтр по статусу'}
            className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase transition ${
              on ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
            }`}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function Pills<T extends string | undefined>({ options, value, onChange }:
  { options: Array<{ value?: T; label: string }>; value: T | undefined; onChange: (v: T | undefined) => void }) {
  return (
    <div className="flex gap-1">
      {options.map(o => (
        <button key={o.label} onClick={() => onChange(o.value)}
          className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase transition ${
            value === o.value ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
          }`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

interface VTProps {
  items: TicketBrief[];
  loading: boolean;
  total: number;
  sortBy: string;
  sortDir: 'asc' | 'desc';
  onSort: (col: any, dir: 'asc' | 'desc') => void;
  isStaff: boolean;
  staff: AuthUser[];
  onAssign: (ticketId: string, assigneeId: string | null) => void;
  onLoadMore: () => void;
}

function VirtualizedTable({ items, loading, total, sortBy, sortDir, onSort, isStaff, staff, onAssign, onLoadMore }: VTProps) {
  const parentRef = useRef<HTMLDivElement>(null);
  const ROW_H = 96;  // верх 44px (колонки) + низ ~50px (контрастное превью)
  const rowVirtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_H,
    overscan: 12,
  });

  // Возврат на то же место списка после захода в тикет: одних фильтров мало,
  // при 700+ тикетах иначе всё равно приходится долистывать заново.
  const restored = useRef(false);
  useEffect(() => {
    if (restored.current || !items.length || !parentRef.current) return;
    const saved = Number(sessionStorage.getItem('queue.scrollTop') || 0);
    if (saved > 0) parentRef.current.scrollTop = saved;
    restored.current = true;
  }, [items.length]);

  const headerCol = (key: string, label: string, w: string) => {
    const active = sortBy === key;
    return (
      <button onClick={() => onSort(key, active && sortDir === 'desc' ? 'asc' : 'desc')}
        className={`text-left text-[10px] uppercase tracking-wider font-bold px-3 py-2 transition ${w} ${
          active ? 'text-blue-400' : 'text-slate-500 hover:text-slate-300'
        }`}>
        {label} {active && (sortDir === 'desc' ? <ChevronDown size={10} className="inline" /> : <ChevronUp size={10} className="inline" />)}
      </button>
    );
  };

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-x-auto">
      <div className="flex bg-slate-950/50 border-b border-slate-800 min-w-[1100px]">
        {headerCol('number',     '№',            'w-20 shrink-0')}
        <div className="flex-1">{headerCol('priority', 'Тема',     'w-full')}</div>
        <div className="w-40 shrink-0 px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Клиент</div>
        <div className="w-32 shrink-0 px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Поддержка</div>
        <div className="w-32 shrink-0 px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Автор</div>
        {headerCol('status',     'Статус',       'w-24 shrink-0 text-center')}
        <div className="w-24 shrink-0 px-2 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold text-center">SLA</div>
        <div className="w-36 shrink-0 px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Агент</div>
        {headerCol('created_at', 'Создан',       'w-24 shrink-0')}
      </div>
      <div ref={parentRef} className="overflow-auto min-w-[1100px]"
           onScroll={(e) => {
             const el = e.currentTarget;
             try { sessionStorage.setItem('queue.scrollTop', String(el.scrollTop)); } catch { /* приватный режим */ }
             const near = el.scrollHeight - el.scrollTop - el.clientHeight < 400;
             if (near && !loading && items.length < total) onLoadMore();
           }}
           style={{ height: 'calc(100vh - 280px)', minHeight: 300 }}>
        {loading && items.length === 0 ? (
          <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-blue-400" /></div>
        ) : items.length === 0 ? (
          <div className="py-10 text-center text-slate-500 text-sm">Ничего не найдено</div>
        ) : (
          <div style={{ height: rowVirtualizer.getTotalSize(), width: '100%', position: 'relative' }}>
            {rowVirtualizer.getVirtualItems().map(vr => {
              const t = items[vr.index];
              return (
                <Row key={t.id} t={t} top={vr.start} height={ROW_H} isStaff={isStaff} staff={staff} onAssign={onAssign} striped={vr.index % 2 === 1} />
              );
            })}
            {items.length < total && (
              <div className="absolute bottom-0 left-0 right-0 py-3 flex justify-center text-[10px] text-slate-500">
                {loading ? <><Loader2 size={11} className="animate-spin mr-1.5" /> подгружаю…</>
                         : `прокрутите вниз — ещё ${total - items.length}`}
              </div>
            )}
          </div>
        )}
      </div>
      <div className="px-3 py-1.5 border-t border-slate-800">
        <span className="text-[10px] text-slate-600">Показано {items.length} из {total}</span>
      </div>
    </div>
  );
}

const PRIORITY_BAR: Record<string, string> = {
  P1: 'bg-red-500', P2: 'bg-orange-500', P3: 'bg-blue-500', P4: 'bg-slate-500',
};

/**
 * Stale-подсветка для давно неподвижных тикетов. Только для «живых»
 * статусов (new/open/pending_customer/on_hold). По приоритету:
 *   P1: ≥2 дня без updated_at → красная;
 *   P2: ≥4 дня;
 *   P3: ≥10 дней;
 *   P4: ≥21 день.
 */
function staleClass(t: TicketBrief): { row?: string; tag?: string } {
  if (['closed', 'resolved', 'cancelled'].includes(t.status)) return {};
  const days = (Date.now() - new Date(t.updated_at).getTime()) / 86400000;
  const limits: Record<string, number> = { P1: 2, P2: 4, P3: 10, P4: 21 };
  const lim = limits[t.priority] ?? 14;
  if (days < lim) return {};
  const severe = days >= lim * 2;
  return {
    row: severe ? 'ring-1 ring-red-700/60 bg-red-950/30' : 'ring-1 ring-amber-700/40 bg-amber-950/20',
    tag: severe ? 'bg-red-900/60 text-red-200 border-red-700' : 'bg-amber-900/40 text-amber-300 border-amber-700',
  };
}

function Row({ t, top, height, isStaff, staff, onAssign, striped }:
  { t: TicketBrief; top: number; height: number; isStaff: boolean; staff: AuthUser[]; onAssign: (id: string, a: string | null) => void; striped?: boolean }) {
  const stale = staleClass(t);
  const staleDays = Math.floor((Date.now() - new Date(t.updated_at).getTime()) / 86400000);
  return (
    <div style={{ position: 'absolute', top, left: 0, right: 0, height }}
      className={`flex flex-col border-b border-slate-800 hover:bg-slate-700/40 transition relative ${
        stale.row || (striped ? 'bg-slate-800/40' : 'bg-slate-950/40')
      }`}>
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${PRIORITY_BAR[t.priority]}`} />
      {/* Верх — колонки */}
      <div className="flex items-center h-11">

      <Link to={`/tickets/${t.id}`} className="w-20 shrink-0 px-3 font-mono text-[11px] text-blue-300 truncate">
        {ticketNumber(t.number)}
      </Link>

      <Link to={`/tickets/${t.id}`}
            className="flex-1 px-3 min-w-0 flex items-center gap-2">
        <span className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold ${PRIORITY_COLOR[t.priority]}`}>{t.priority}</span>
        {t.has_new_client_message && (
          <span title="Новое сообщение от клиента"
                className="w-2 h-2 rounded-full bg-blue-400 shrink-0 animate-pulse" />
        )}
        <span title={t.subject}
              className={`text-sm text-white truncate ${t.has_new_client_message ? 'font-bold' : ''}`}>
          {t.subject || <span className="text-red-400 italic">(без заголовка)</span>}
        </span>
        {stale.tag && (
          <span title={`Без движения ${staleDays} дн.`}
                className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase border ${stale.tag}`}>
            stale {staleDays}д
          </span>
        )}
      </Link>

      <div className="w-40 shrink-0 px-3 truncate">
        {t.organization_name ? (
          <Link to={`/organizations/${t.organization_id}`} className="block group" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-1">
              {t.contract_active === false && (
                <span title="Договор не активен/истёк" className="text-red-400 text-[10px]">●</span>
              )}
              {t.contract_status === 'expiring_soon' && (
                <span title="Скоро истекает" className="text-yellow-400 text-[10px]">●</span>
              )}
              <span className={`text-xs truncate ${t.contract_active === false ? 'text-red-300' : 'text-slate-200 group-hover:text-blue-400'}`}>
                {t.organization_name}
              </span>
            </div>
            {t.organization_inn && <div className="text-[10px] font-mono text-slate-600 truncate">{t.organization_inn}</div>}
          </Link>
        ) : <span className="text-slate-700 text-xs">—</span>}
      </div>

      <div className="w-32 shrink-0 px-3">
        {t.project_status ? (
          <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold border ${PROJECT_BADGE[t.project_status]}`}>
            {PROJECT_LABEL[t.project_status]}
          </span>
        ) : <span className="text-slate-700 text-[10px]">—</span>}
        {t.contract_status === 'expiring_soon' && (
          <div className="text-[10px] text-yellow-400 mt-0.5">договор истекает</div>
        )}
      </div>

      <div className="w-32 shrink-0 px-3 text-xs text-slate-400 truncate">
        {t.requester_name || t.requester_email || <span className="text-slate-700">—</span>}
        {t.requester_email && t.requester_name && (
          <div className="text-[10px] text-slate-600 truncate">{t.requester_email}</div>
        )}
      </div>

      <div className="w-24 shrink-0 px-2">
        <span className={`block w-full text-center px-1 py-0.5 rounded text-[10px] font-bold uppercase border truncate ${ticketStatusColor(t)}`}>
          {ticketStatusLabel(t)}
        </span>
      </div>

      <div className="w-24 shrink-0 px-2">
        {t.sla_state && t.sla_label ? (
          <span className={`block w-full text-center px-1 py-0.5 rounded text-[10px] font-mono border truncate ${SLA_COLOR[t.sla_state]}`}
                title={t.sla_state === 'ok' ? 'В рамках SLA' : `SLA: ${t.sla_state}`}>
            {t.sla_label}
          </span>
        ) : (
          <span className="block w-full text-center text-slate-700 text-[10px]">—</span>
        )}
      </div>

      <div className="w-36 shrink-0 px-3 text-xs text-slate-400 truncate flex items-center gap-1">
        {t.assignee_id ? <UserCheck size={11} className="text-green-500 shrink-0" /> : <UserX size={11} className="text-slate-600 shrink-0" />}
        {isStaff ? (
          <select value={t.assignee_id || ''}
            onChange={e => onAssign(t.id, e.target.value || null)}
            className="bg-transparent border border-slate-800 rounded px-1 py-0.5 text-[11px] text-slate-300 hover:border-slate-600 focus:border-blue-500 outline-none w-full min-w-0">
            <option value="">— не назначен —</option>
            {staff.map(s => <option key={s.id} value={s.id}>{s.full_name || s.email}</option>)}
          </select>
        ) : (
          <span className="truncate">{t.assignee_name || t.assignee_email || <span className="text-slate-600">—</span>}</span>
        )}
      </div>

      <Link to={`/tickets/${t.id}`} className="w-24 shrink-0 px-3 text-[11px] text-slate-500">
        {fmtTime(t.created_at)}
      </Link>
      </div>

      {/* Низ — превью описания, контрастный, на лёгком фоне */}
      {t.body_excerpt && (
        <Link to={`/tickets/${t.id}`} title={t.body_excerpt}
              className="block ml-1 mr-2 mb-1 px-3 py-1.5 rounded
                         bg-slate-800/50 hover:bg-slate-800
                         text-[12.5px] text-slate-200 leading-snug
                         border-l-2 border-slate-700"
              style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {t.body_excerpt}
        </Link>
      )}
    </div>
  );
}
